interface WhiteboardCacheEntry {
  elements: readonly any[];
  appState: any;
  timestamp: number;
}

const WHITEBOARD_CACHE_PREFIX = 'whiteboard_cache_';
const CACHE_EXPIRY_HOURS = 3;

export class WhiteboardCache {
  private static instance: WhiteboardCache;
  private cache: Map<string, WhiteboardCacheEntry> = new Map();

  private constructor() {
    this.loadFromStorage();
    this.cleanupExpiredEntries();
    // Clean up expired entries every hour
    setInterval(() => this.cleanupExpiredEntries(), 60 * 60 * 1000);
  }

  public static getInstance(): WhiteboardCache {
    if (!WhiteboardCache.instance) {
      WhiteboardCache.instance = new WhiteboardCache();
    }
    return WhiteboardCache.instance;
  }

  private getCacheKey(lessonId: string): string {
    return `${WHITEBOARD_CACHE_PREFIX}${lessonId}`;
  }

  private loadFromStorage(): void {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(WHITEBOARD_CACHE_PREFIX)) {
          const lessonId = key.replace(WHITEBOARD_CACHE_PREFIX, '');
          const cachedData = localStorage.getItem(key);
          if (cachedData) {
            const entry: WhiteboardCacheEntry = JSON.parse(cachedData);
            this.cache.set(lessonId, entry);
          }
        }
      }
    } catch (error) {
      console.error('Error loading whiteboard cache from storage:', error);
    }
  }

  private saveToStorage(lessonId: string, entry: WhiteboardCacheEntry): void {
    try {
      const key = this.getCacheKey(lessonId);
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.error('Error saving whiteboard cache to storage:', error);
    }
  }

  private cleanupExpiredEntries(): void {
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;

    for (const [lessonId, entry] of this.cache.entries()) {
      if (now - entry.timestamp > expiryTime) {
        this.cache.delete(lessonId);
        try {
          const key = this.getCacheKey(lessonId);
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing expired cache entry:', error);
        }
      }
    }
  }

  public getElements(lessonId: string): readonly any[] | null {
    const entry = this.cache.get(lessonId);
    if (!entry) return null;

    // Check if entry is expired
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
    
    if (now - entry.timestamp > expiryTime) {
      this.cache.delete(lessonId);
      try {
        const key = this.getCacheKey(lessonId);
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Error removing expired cache entry:', error);
      }
      return null;
    }

    return entry.elements;
  }

  public getAppState(lessonId: string): any | null {
    const entry = this.cache.get(lessonId);
    if (!entry) return null;

    // Check if entry is expired
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
    
    if (now - entry.timestamp > expiryTime) {
      this.cache.delete(lessonId);
      try {
        const key = this.getCacheKey(lessonId);
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Error removing expired cache entry:', error);
      }
      return null;
    }

    return entry.appState;
  }

  public setElements(lessonId: string, elements: readonly any[], appState?: any): void {
    const entry: WhiteboardCacheEntry = {
      elements,
      appState: appState || { viewBackgroundColor: "#ffffff" },
      timestamp: Date.now()
    };
    
    this.cache.set(lessonId, entry);
    this.saveToStorage(lessonId, entry);
  }

  public setAppState(lessonId: string, appState: any): void {
    const existingEntry = this.cache.get(lessonId);
    const entry: WhiteboardCacheEntry = {
      elements: existingEntry?.elements || [],
      appState: appState || { viewBackgroundColor: "#ffffff" },
      timestamp: Date.now()
    };
    
    this.cache.set(lessonId, entry);
    this.saveToStorage(lessonId, entry);
  }

  public clearCache(lessonId?: string): void {
    if (lessonId) {
      this.cache.delete(lessonId);
      try {
        const key = this.getCacheKey(lessonId);
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Error clearing cache for lesson:', error);
      }
    } else {
      this.cache.clear();
      try {
        // Remove all whiteboard cache entries from localStorage
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith(WHITEBOARD_CACHE_PREFIX)) {
            localStorage.removeItem(key);
          }
        }
      } catch (error) {
        console.error('Error clearing all whiteboard cache:', error);
      }
    }
  }

  public getCacheSize(): number {
    return this.cache.size;
  }
}

export const whiteboardCache = WhiteboardCache.getInstance();
